import { useState, useCallback, useEffect } from 'react';
import {
  EditOutlined,
  FileTextOutlined,
  PlusCircleOutlined,
  RedoOutlined,
} from '@ant-design/icons';
import {
  ATable,
  DropdownOptions,
  MenuOptions,
  PageIntro,
  PageLayout,
  PagePanel,
  StatusActive,
} from '../../components';
import { useToast } from '../../hooks/toast';
import api from '../../services/api';
import { useNavigate } from 'react-router-dom';
import { IModelHealthCertificate } from '../../shared/interfaces';
import { useAuth } from '../../hooks/auth';
import { PermissionActions } from '../../shared/enums/permissions.enum';
import { useI18n } from '../../shared/i18n';

const ModelsHealthCertificates: React.FC = () => {
  const { addToast } = useToast();
  const navigate = useNavigate();
  const { permissionFind } = useAuth();
  const { t } = useI18n();
  const [search, setSearch] = useState<string>('');
  const [modelshealthCertificates, setModelsHealthCertificates] = useState<
    IModelHealthCertificate[]
  >([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [limit, setLimit] = useState<number>(10);
  const [page, setPage] = useState<number>(1);
  const [pages, setPages] = useState<number>();

  const listModelsHealthCertificates = useCallback(async () => {
    setLoading(true);
    setModelsHealthCertificates([]);
    try {
      const response = await api.post('/health-certificates/list', {
        take: limit,
        skip: (page - 1) * limit,
        keyword: search,
      });
      setPages(response.data.count);
      setModelsHealthCertificates(response.data.data);
    } catch (err) {
      addToast({
        type: 'error',
        title: t('modelsHealthCertificatesPage.toast.errorTitle'),
        description: t('modelsHealthCertificatesPage.toast.listError'),
      });
    } finally {
      setLoading(false);
    }
  }, [limit, search, page, addToast, t]);

  useEffect(() => {
    listModelsHealthCertificates();
  }, [page, search, limit, listModelsHealthCertificates]);

  return (
    <PageLayout>
      <PageIntro
        badge={t('modelsHealthCertificatesPage.intro.badge')}
        icon={<FileTextOutlined />}
        title={t('modelsHealthCertificatesPage.intro.title')}
        description={t('modelsHealthCertificatesPage.intro.description')}
      />
      <MenuOptions
        menu_items={[
          {
            label: t('modelsHealthCertificatesPage.actions.refresh'),
            key: '1',
            icon: <RedoOutlined />,
            onClick: () => listModelsHealthCertificates(),
          },
          permissionFind([PermissionActions.modelsHealthCertificatesCreate])
            ? {
                label: t('modelsHealthCertificatesPage.actions.create'),
                key: '2',
                icon: <PlusCircleOutlined />,
                onClick: () => navigate('/model_health_certificate/new'),
              }
            : null,
        ]}
        showInputSearch
        search={search}
        setSearch={setSearch}
      />
      <PagePanel>
        <ATable
          scroll={{ y: 510 }}
          columns={[
            {
              title: t('modelsHealthCertificatesPage.table.index'),
              width: 100,
              render: (value, record, index) => <>{index + 1}</>,
            },
            {
              title: t('modelsHealthCertificatesPage.table.title'),
              dataIndex: 'title',
            },
            {
              title: t('modelsHealthCertificatesPage.table.status'),
              dataIndex: 'status',
              width: 95,
              render: (value: boolean) => <StatusActive status={value} />,
            },
            {
              title: t('modelsHealthCertificatesPage.table.options'),
              width: 130,
              render: (cell: IModelHealthCertificate) => (
                <DropdownOptions
                  items={[
                    permissionFind([
                      PermissionActions.modelsHealthCertificatesUpdate,
                    ])
                      ? {
                          label: t('modelsHealthCertificatesPage.actions.edit'),
                          key: '1',
                          icon: <EditOutlined />,
                          onClick: () => {
                            navigate(`/model_health_certificate/${cell.id}`);
                          },
                        }
                      : null,
                  ]}
                />
              ),
            },
          ]}
          loading={loading}
          dataSource={modelshealthCertificates}
          page={page}
          setPage={setPage}
          pages={pages}
          limit={limit}
          setLimit={setLimit}
        />
      </PagePanel>
    </PageLayout>
  );
};

export default ModelsHealthCertificates;
